import * as T from 'three';
// Crunchy candy shells for the Halloween toys. The shell is the usual forty
// pre-fractured pieces (scripts/new-experience-models.py); what is new is how
// it breaks. While a press loads the shell, crack lines race out from the
// finger along the piece borders and as fine hairlines, each step with a tiny
// hitstop and a puff of crumbs; cracked pieces sink and tilt a little; then
// pieces snap off as rigid shards that tumble, bounce on the floor and settle.
// The shell shader also paints the face, wings and lining, so they break with
// the pieces. Shape numbers match the Blender builders.
export const SHAPES={
  pumpkin:{W:.98,H:.70,C:.76,bottom:.12,maxX:.98*1.075},
  // Ears: cones along the unit directions at polar angle earV, earU either
  // side of the front, earR wide, flattened front to back by earFlat, and
  // ear (a fraction of the radius) tall.
  bat:{W:.80,H:.84,C:.90,bottom:.06,maxX:.80,earV:.38,earU:1.15,earR:.34,earFlat:.66,ear:.56,earTip:.03},
  // A hand-packed snowball (snowbunny in scripts/new-experience-models.py), lumps averaged out.
  snow:{W:.85,H:.78,C:.80,bottom:.1292,maxX:.869},
};
const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
const NOISE=`
float crackHash(vec3 p){p=fract(p*.3183099+.1);p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}
float crackNoise(vec3 x){vec3 i=floor(x),f=fract(x);f=f*f*(3.-2.*f);
  return mix(mix(mix(crackHash(i),crackHash(i+vec3(1,0,0)),f.x),mix(crackHash(i+vec3(0,1,0)),crackHash(i+vec3(1,1,0)),f.x),f.y),
    mix(mix(crackHash(i+vec3(0,0,1)),crackHash(i+vec3(1,0,1)),f.x),mix(crackHash(i+vec3(0,1,1)),crackHash(i+vec3(1,1,1)),f.x),f.y),f.z);}
float discMask(vec2 p,vec2 c,vec2 r,float w){vec2 q=(p-c)/r;return 1.-smoothstep(1.-w,1.+w,length(q));}
// A crack is a zigzag: straight runs with a kink every SEG (in shell radii),
// offsets seeded per crack, so a crack keeps its shape while it grows.
float crackOffset(float seed,float t){const float SEG=.042;float x=max(t,0.)/SEG,i=floor(x);
  return (mix(crackHash(vec3(seed,i,7.)),crackHash(vec3(seed,i+1.,7.)),x-i)-.5)*SEG*.95*smoothstep(0.,SEG,t);}
// Distance to a crack from a along dir, len long; along: 0 at a, 1 at the tip.
float crackSegment(vec2 q,vec2 a,vec2 dir,float len,float seed,out float along){
  vec2 rel=q-a;float t=dot(rel,dir);along=t/len;
  if(t<0.||t>len)return 1.;
  return abs(dot(rel,vec2(-dir.y,dir.x))-crackOffset(seed,t));}
`;
// Face, wings and lining in face coordinates f (arc lengths from the front centre).
const PAINT={
  snow:`
    lining=vec3(.86,.91,1.);`,
  pumpkin:`
    float eyes=0.,glints=0.,blush=0.,mouth=0.;
    for(int k=0;k<2;k++){float s=k==0?-1.:1.;
      eyes=max(eyes,discMask(f,vec2(s*.21,.08),vec2(.085),.08));
      glints=max(glints,discMask(f,vec2(s*.21-.03,.115),vec2(.025),.15));
      blush=max(blush,discMask(f,vec2(s*.38,-.04),vec2(.09,.048),.25));}
    { vec2 q=vec2(f.x/.065,(f.y+.03)/.055);mouth=(1.-smoothstep(.0,.3,abs(length(q)-1.)))*step(f.y,-.03)*step(abs(f.x),.07); }
    if(front){
      paint=mix(paint,vec3(1.,.48,.56),blush*.55);
      paint=mix(paint,vec3(.16,.05,.01),max(eyes,mouth));
      paint=mix(paint,vec3(1.),glints*eyes);
    }
    lining=vec3(1.,.74,.42);`,
  bat:`
    float eyes=0.,glints=0.,blush=0.,fangs=0.,mouth=0.;
    for(int k=0;k<2;k++){float s=k==0?-1.:1.;
      eyes=max(eyes,discMask(f,vec2(s*.17,.06),vec2(.085,.092),.08));
      glints=max(glints,discMask(f,vec2(s*.17-.03,.10),vec2(.026),.15));
      blush=max(blush,discMask(f,vec2(s*.32,-.05),vec2(.075,.04),.25));
      fangs=max(fangs,discMask(f,vec2(s*.035,-.135),vec2(.014,.026),.2)*step(f.y,-.12));}
    { vec2 q=vec2(f.x/.05,(f.y+.10)/.03);mouth=(1.-smoothstep(.0,.35,abs(length(q)-1.)))*step(f.y,-.10)*step(abs(f.x),.055); }
    // Inner ears: a band down the front of each ear cone, inset from its
    // edges and stopping short of the tip and the base.
    float innerEar=0.;
    for(int k=0;k<2;k++){float s=k==0?-1.:1.;
      vec3 e=vec3(sin(${SHAPES.bat.earV})*cos(1.5708+s*${SHAPES.bat.earU.toFixed(3)}),cos(${SHAPES.bat.earV}),sin(${SHAPES.bat.earV})*sin(1.5708+s*${SHAPES.bat.earU.toFixed(3)}));
      vec3 dq=dir-e;float rho=length(vec3(dq.xy,dq.z/${SHAPES.bat.earFlat}));
      float facing=dq.z/max(length(dq),1e-5);
      innerEar=max(innerEar,(1.-smoothstep(${(SHAPES.bat.earR*.7).toFixed(4)},${(SHAPES.bat.earR*.78).toFixed(4)},rho))*smoothstep(.62,.76,facing)*smoothstep(.015,.05,rho));}
    // Folded wings like a cloak (scripts/new-experience-models.py bat_wing):
    // scalloped lower edges and two finger bones fanning from the wrist near
    // the front tip.
    float wing=0.,bones=0.;
    for(int k=0;k<2;k++){
      float a=k==0?u:3.14159-u;a=atan(sin(a),cos(a));float t=(a+.3)/1.55,c=clamp(t,0.,1.);
      float top=.95+pow(c,1.6),bottom=2.45-.18*abs(sin(3.14159*3.*t))-.25*t*t;
      float inside=min(min(v-top,bottom-v),min(t*1.55,(1.-t)*1.55));
      wing=max(wing,smoothstep(0.,.015,inside));
      vec2 x=vec2(t*1.55,v),w=vec2(.96*1.55,1.99);
      for(int j=1;j<3;j++){float cusp=float(j)/3.;vec2 d=vec2(cusp*1.55,2.45-.25*cusp*cusp)-w;
        float h=clamp(dot(x-w,d)/dot(d,d),0.,1.);
        bones=max(bones,smoothstep(0.,.006,min(inside,.017-length(x-w-d*h))));}
    }
    paint=mix(paint,vec3(.244,.169,.367),wing*.95);
    paint=mix(paint,vec3(.447,.351,.579),bones*.9);
    paint=mix(paint,vec3(.93,.62,.82),innerEar*.9);
    if(front){
      paint=mix(paint,vec3(1.,.52,.68),blush*.5);
      paint=mix(paint,vec3(.05,.02,.06),max(eyes,mouth));
      paint=mix(paint,vec3(1.),max(glints*eyes,fangs));
    }
    lining=vec3(.10,.05,.14);`,
};
// The bat's outer surface, as in scripts/new-experience-models.py: points on
// the shape for unit directions, so the shader can rebuild its normal.
const B=SHAPES.bat,f=v=>v.toFixed(4);
const BAT_SURFACE=`
float batEar(vec3 d){float h=0.;
  for(int s=0;s<2;s++){float sg=s==0?-1.:1.;
    vec3 e=vec3(sin(${f(B.earV)})*cos(1.5708+sg*${f(B.earU)}),cos(${f(B.earV)}),sin(${f(B.earV)})*sin(1.5708+sg*${f(B.earU)})),q=d-e;
    float rho=length(vec3(q.xy,q.z/${f(B.earFlat)}));rho=sqrt(rho*rho+${f(B.earTip*B.earTip)})-${f(B.earTip)};
    if(rho<${f(B.earR)})h=max(h,${f(B.ear)}*pow(max(0.,1.-pow(rho/${f(B.earR)},1.5)),1.8));}
  return h;}
vec3 batPoint(vec3 d){float k=1.+batEar(d);return vec3(shapeW*d.x*k,shapeC+shapeH*d.y*k,shapeW*d.z*k);}
`;
// The mesh is coarse for the bat's ear fillets, and interpolated normals
// there drew a sawtooth highlight. The outer surface instead takes the normal
// of the shape itself, per pixel, for the base colour and the clear coat.
const BAT_NORMAL=`
  bool shapeNormal=false;
  {
    vec3 sp=vRest/shapeScale+vec3(0.,shapeShift,0.),fromCentre=sp-vec3(0.,shapeC,0.);
    if(dot(normalize(vRestNormal),normalize(fromCentre))>.35){
      vec3 d=normalize(vec3(sp.x/shapeW,(sp.y-shapeC)/shapeH,sp.z/shapeW)),a=normalize(cross(d,abs(d.y)<.9?vec3(0.,1.,0.):vec3(1.,0.,0.))),b=cross(d,a);
      const float E=.003;
      vec3 n=normalize(cross(batPoint(normalize(d+a*E))-batPoint(normalize(d-a*E)),batPoint(normalize(d+b*E))-batPoint(normalize(d-b*E))));
      if(dot(n,fromCentre)<0.)n=-n;
      normal=normalize(vRestToView*n);shapeNormal=true;
    }
  }`;
// One shared set of uniforms for every piece of a toy's shell.
export function shellUniforms(shape,scale,limit=16){
  return {crackOrigins:{value:Array.from({length:limit},()=>new T.Vector4(0,0,0,0))},shapeScale:{value:scale},
    shapeShift:{value:shape.bottom},shapeW:{value:shape.W},shapeH:{value:shape.H},shapeC:{value:shape.C}};
}
export function patchShell(material,kind,uniforms){
  material.customProgramCacheKey=()=>'candy-shell-'+kind;
  material.onBeforeCompile=shader=>{
    Object.assign(shader.uniforms,uniforms);
    shader.vertexShader='attribute float crackRim;\nvarying float vRim;\nvarying vec3 vRest;\nvarying vec3 vRestNormal;\nvarying mat3 vRestToView;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\n  vRim=crackRim;vRest=position;vRestNormal=normal;vRestToView=normalMatrix;');
    let fragment=shader.fragmentShader;
    if(kind==='bat')fragment=fragment.replace('#include <normal_fragment_maps>','#include <normal_fragment_maps>'+BAT_NORMAL)
      .replace('#include <clearcoat_normal_fragment_begin>','#include <clearcoat_normal_fragment_begin>\n#ifdef USE_CLEARCOAT\n  if(shapeNormal)clearcoatNormal=normal;\n#endif');
    shader.fragmentShader=`uniform vec4 crackOrigins[${uniforms.crackOrigins.value.length}];uniform float shapeScale;uniform float shapeShift;uniform float shapeW;uniform float shapeH;uniform float shapeC;
      varying float vRim;varying vec3 vRest;varying vec3 vRestNormal;varying mat3 vRestToView;${NOISE}${kind==='bat'?BAT_SURFACE:''}`+fragment.replace('#include <color_fragment>',`#include <color_fragment>
      {
        vec3 sp=vRest/shapeScale+vec3(0.,shapeShift,0.);
        // Direction on the unit sphere of the shape's parameters; the bat's
        // ears scale it outward, so normalise rather than assume the surface.
        vec3 dir=normalize(vec3(sp.x/shapeW,(sp.y-shapeC)/shapeH,sp.z/shapeW));
        float u=atan(sp.z,sp.x),v=${kind==='bat'?'acos(clamp(dir.y,-1.,1.))':'acos(clamp((sp.y-shapeC)/shapeH,-1.,1.))'};
        vec2 f=vec2((u-1.5708)*shapeW,(1.5708-v)*shapeH);bool front=sp.z>0.;
        // Paint in sRGB, like the Blender preview colours, then return to linear.
        vec3 paint=pow(max(diffuseColor.rgb,0.),vec3(1./2.2)),lining=paint;
        ${PAINT[kind]}
        // The inside of the shell (faces turned toward the centre) shows the lining.
        float inner=step(dot(normalize(vRestNormal),normalize(sp-vec3(0.,shapeC,0.))),-.2);
        diffuseColor.rgb=pow(mix(paint,lining,inner),vec3(2.2));
        // Cracks run out from each press point the way a brittle shell
        // breaks under a point load: six jagged radial cracks, each with one
        // branch, and ring cracks joining neighbouring radials in some
        // sectors. Inside the jagged front, the borders where pieces will
        // part show too. Lines are dark with a pale stressed edge.
        float crackFront=0.,gap=1.,frontNoise=crackNoise(sp*8.)-.5;
        for(int i=0;i<${uniforms.crackOrigins.value.length};i++){vec4 o=crackOrigins[i];if(o.w<=0.)continue;
          vec3 op=o.xyz/shapeScale+vec3(0.,shapeShift,0.),dl=sp-op;float R=o.w/shapeScale;
          crackFront=max(crackFront,1.-smoothstep(R*.82,R,length(dl)+frontNoise*R*.35));
          if(dot(dl,dl)>R*R*1.1)continue;
          vec3 n=normalize(op-vec3(0.,shapeC,0.)),t1=normalize(cross(n,abs(n.y)<.95?vec3(0.,1.,0.):vec3(1.,0.,0.))),t2=cross(n,t1);
          vec2 q=vec2(dot(dl,t1),dot(dl,t2));
          float seed=floor(crackHash(op*17.31+.5)*997.),base=crackHash(vec3(seed,1.,2.))*6.2832,along;
          for(int k=0;k<6;k++){float fk=float(k),cs=seed*7.+fk;
            float ang=base+fk*1.0472+(crackHash(vec3(seed,fk,3.))-.5)*.7,len=R*(.55+.45*crackHash(vec3(seed,fk,4.)));vec2 dir=vec2(cos(ang),sin(ang));
            gap=min(gap,crackSegment(q,vec2(0.),dir,len,cs,along)/mix(1.,.3,clamp(along,0.,1.)));
            float tb=len*(.3+.35*crackHash(vec3(seed,fk,5.))),ba=ang+(crackHash(vec3(seed,fk,6.))<.5?-1.:1.)*(.4+.4*crackHash(vec3(seed,fk,8.)));
            gap=min(gap,crackSegment(q,dir*tb+vec2(-dir.y,dir.x)*crackOffset(cs,tb),vec2(cos(ba),sin(ba)),(len-tb)*(.4+.4*crackHash(vec3(seed,fk,9.))),cs+50.,along)/mix(.75,.25,clamp(along,0.,1.)));
          }
          float r=length(q),th=atan(q.y,q.x),sector=floor(mod(th-base,6.2832)/1.0472);
          for(int j=0;j<2;j++){float fj=float(j);
            if(crackHash(vec3(seed,sector,11.+fj))<.4)continue;
            float x=(th+3.1416)/.3,ii=floor(x),wob=(mix(crackHash(vec3(seed+fj,ii,15.)),crackHash(vec3(seed+fj,ii+1.,15.)),x-ii)-.5)*R*.09;
            gap=min(gap,abs(r-R*(j==0?.34:.64)*(.9+.2*crackHash(vec3(seed,sector,13.+fj)))-wob)*2.);
          }
        }
        // Derivatives outside the loop: the loop's branches differ per pixel.
        float px=max(fwidth(gap),1e-5),w=.0034;
        float line=(1.-smoothstep(w-px,w+px,gap))*(1.-inner),halo=(1.-smoothstep(w*2.5-px,w*3.5+px,gap))*(1.-inner)-line;
        float border=smoothstep(.9,.985,vRim)*crackFront*(1.-inner);
        diffuseColor.rgb=mix(diffuseColor.rgb,mix(diffuseColor.rgb,vec3(1.),.4),halo*.45);
        diffuseColor.rgb=mix(diffuseColor.rgb,diffuseColor.rgb*${kind==='snow'?'vec3(.62,.68,.8)':'vec3(.22)'},max(line,border*.75));
      }`);
  };
  material.needsUpdate=true;
}
// Crack fronts: each grows quickly to its reach. Old cracks stay; the oldest
// gives way once all slots are used.
export class CrackWeb{
  constructor(uniform){this.slots=uniform.value;this.list=[];this.clock=0;}
  add(point,reach){
    if(this.list.length>=this.slots.length)this.list.shift();
    this.list.push({point:[...point],reach,born:this.clock});
  }
  radius(c){return c.reach*(1-Math.exp(-(this.clock-c.born)/.05));}
  step(dt){
    this.clock+=dt;
    for(let i=0;i<this.slots.length;i++){const c=this.list[i];if(c)this.slots[i].set(c.point[0],c.point[1],c.point[2],this.radius(c));else this.slots[i].set(0,0,0,0);}
  }
  // 0..1: how far the cracks have reached a point.
  at(p){let best=0;for(const c of this.list){const r=this.radius(c),d=Math.hypot(p[0]-c.point[0],p[1]-c.point[1],p[2]-c.point[2]);best=Math.max(best,clamp((r-d)/(r*.3+1e-6),0,1));}return best;}
  clear(){this.list.length=0;this.step(0);}
}
// Per-vertex border weight: 0 at a piece's middle, 1 at its edge, measured as
// the angle from the shell centre.
export function rimWeights(geometry,pieceCenter,shellCenter){
  const p=geometry.attributes.position.array,n=p.length/3,rim=new Float32Array(n);
  const c=new T.Vector3().fromArray(pieceCenter).sub(shellCenter).normalize(),v=new T.Vector3();let most=1e-6;
  for(let i=0;i<n;i++){v.set(p[i*3]-shellCenter.x,p[i*3+1]-shellCenter.y,p[i*3+2]-shellCenter.z).normalize();rim[i]=Math.acos(clamp(v.dot(c),-1,1));most=Math.max(most,rim[i]);}
  for(let i=0;i<n;i++)rim[i]/=most;
  geometry.setAttribute('crackRim',new T.BufferAttribute(rim,1));
}
// Rigid shards: a few points from each piece touch the floor; gravity, a
// bounce, friction and spin damping until they rest.
export class Shards{
  // onImpact(i, speed) may return true to shatter a shard where it lands.
  constructor(pieces,{gravity=14,random=Math.random,onImpact=null}={}){
    this.random=random;this.gravity=gravity;this.onImpact=onImpact;
    this.items=pieces.map(({center,points,normal=[0,1,0]})=>({rest:[...center],points,normal,p:null,v:[0,0,0],q:new T.Quaternion(),w:[0,0,0],asleep:true,quiet:0,landed:false,gone:false}));
    this.euler=new T.Euler();this.tmp=new T.Vector3();this.spin=new T.Quaternion();
  }
  launch(i,from,velocity,spin){const s=this.items[i];s.p=[...from];s.v=[...velocity];s.w=[...spin];s.q.identity();s.asleep=false;s.quiet=0;s.landed=false;s.gone=false;}
  get active(){return this.items.some(s=>s.p&&!s.asleep);}
  step(dt){
    const h=Math.min(dt,1/30)/2;
    for(let sub=0;sub<2;sub++)for(const s of this.items){
      if(!s.p||s.asleep)continue;
      s.v[1]-=this.gravity*h;for(let a=0;a<3;a++)s.p[a]+=s.v[a]*h;
      const wl=Math.hypot(...s.w);
      if(wl>1e-6){this.spin.setFromAxisAngle(this.tmp.set(s.w[0]/wl,s.w[1]/wl,s.w[2]/wl),wl*h);s.q.premultiply(this.spin).normalize();}
      // Lowest contact point below the floor: lift, bounce and rub.
      let low=0;for(const pt of s.points){this.tmp.fromArray(pt).applyQuaternion(s.q);low=Math.min(low,s.p[1]+this.tmp.y);}
      if(low<0){
        // A hard first landing may shatter the shard into crumbs.
        if(!s.landed){s.landed=true;if(this.onImpact?.(this.items.indexOf(s),-s.v[1])){s.gone=true;s.asleep=true;continue;}}
        s.p[1]-=low;if(s.v[1]<0)s.v[1]*=-.28;
        const rub=Math.exp(-10*h);s.v[0]*=rub;s.v[2]*=rub;for(let a=0;a<3;a++)s.w[a]*=Math.exp(-7*h);
        // A thin curved shard does not stand on its edge: tip it until it lies face up or down.
        const n=this.tmp.fromArray(s.normal).applyQuaternion(s.q),up=n.y>=0?1:-1;
        if(Math.abs(n.y)<.92){s.w[0]+=-n.z*up*30*h;s.w[2]+=n.x*up*30*h;s.quiet=0;}
      }
      const motion=Math.hypot(...s.v)+Math.hypot(...s.w)*.1;
      s.quiet=motion<.05&&low<=0?s.quiet+h:0;if(s.quiet>.35){s.asleep=true;s.v=[0,0,0];s.w=[0,0,0];}
    }
  }
  pose(i){const s=this.items[i];if(!s.p)return null;this.euler.setFromQuaternion(s.q);return {center:[...s.p],rotation:[this.euler.x,this.euler.y,this.euler.z],scale:s.gone?1e-4:1};}
  reset(){for(const s of this.items){s.p=null;s.asleep=true;s.q.identity();s.landed=false;s.gone=false;}}
}
// Candy crumbs: tiny chips that jump from cracks, bounce once or twice and stay.
export class Crumbs{
  constructor(parent,{count=320,size=.022,colors=['#f08a2c','#ffc070'],gravity=14,random=Math.random}={}){
    this.random=random;this.gravity=gravity;this.size=size;
    const geometry=new T.TetrahedronGeometry(size,0);
    this.mesh=new T.InstancedMesh(geometry,new T.MeshStandardMaterial({roughness:.35}),count);this.mesh.count=0;this.mesh.frustumCulled=false;
    this.mesh.castShadow=true;this.mesh.unpickable=true;this.mesh.name='candy_crumbs';parent.add(this.mesh);
    this.colors=colors.map(c=>new T.Color(c));this.items=[];this.cursor=0;this.matrix=new T.Matrix4();this.q=new T.Quaternion();this.s=new T.Vector3();
    // Give every instance its colour now, so the shader is built with
    // instance colours from the first frame. Adding them at the first burst
    // switched programs mid-run, and in some runs the crumbs then drew black.
    for(let i=0;i<count;i++)this.mesh.setColorAt(i,this.colors[i%this.colors.length]);
  }
  burst(at,direction,amount,speed=1.4){
    for(let n=0;n<amount;n++){
      const i=this.cursor++%this.mesh.instanceMatrix.count,r=this.random;
      const v=[direction[0]*speed*(.4+.8*r())+(r()-.5)*speed,Math.abs(direction[1])*speed*.5+speed*(.3+.7*r()),direction[2]*speed*(.4+.8*r())+(r()-.5)*speed];
      this.items[i]={p:[...at],v,spin:[r()*6,r()*6,r()*6],angle:[r()*6,r()*6,r()*6],scale:.6+.8*r(),rest:false};
      this.mesh.setColorAt(i,this.colors[(this.cursor+n)%this.colors.length]);
      this.mesh.count=Math.max(this.mesh.count,i+1);
    }
    if(this.mesh.instanceColor)this.mesh.instanceColor.needsUpdate=true;
  }
  get active(){return this.items.some(c=>c&&!c.rest);}
  step(dt){
    const h=Math.min(dt,1/30);let changed=false;
    this.items.forEach((c,i)=>{
      if(!c)return;
      if(!c.rest){
        c.v[1]-=this.gravity*h;for(let a=0;a<3;a++){c.p[a]+=c.v[a]*h;c.angle[a]+=c.spin[a]*h;}
        const floor=this.size*.5*c.scale;
        if(c.p[1]<floor){c.p[1]=floor;c.v[1]*=-.3;c.v[0]*=.6;c.v[2]*=.6;c.spin=c.spin.map(s=>s*.5);if(Math.abs(c.v[1])<.15)c.rest=true;}
        changed=true;
      }
      this.q.setFromEuler(new T.Euler(...c.angle));this.s.setScalar(c.scale);
      this.mesh.setMatrixAt(i,this.matrix.compose(new T.Vector3(...c.p),this.q,this.s));
    });
    if(changed)this.mesh.instanceMatrix.needsUpdate=true;
  }
  clear(){this.items.length=0;this.mesh.count=0;this.cursor=0;}
  dispose(){this.mesh.parent?.remove(this.mesh);this.mesh.geometry.dispose();this.mesh.material.dispose();}
}
